import { useState, useEffect } from 'react';
import { Save, Building2, DollarSign, Bell, Info, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const MONEDAS = [
  { code: 'ARS', label: 'Peso argentino', simbolo: '$' },
  { code: 'USD', label: 'Dólar estadounidense', simbolo: 'US$' },
  { code: 'EUR', label: 'Euro', simbolo: '€' },
  { code: 'UYU', label: 'Peso uruguayo', simbolo: '$U' },
  { code: 'BRL', label: 'Real brasilero', simbolo: 'R$' },
];

function SectionCard({ icon: Icon, iconColor, iconBg, title, subtitle, children }) {
  return (
    <div className="card dark:bg-slate-800">
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100 dark:border-slate-700">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon size={16} className={iconColor} />
        </div>
        <div>
          <h2 className="text-sm font-bold text-slate-900 dark:text-white">{title}</h2>
          {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-300">{label}</p>
        {description && <p className="text-xs text-slate-400 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
          checked ? 'bg-primary-500' : 'bg-slate-200 dark:bg-slate-700'
        }`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`} />
      </button>
    </div>
  );
}

export default function Parametros() {
  const { can } = useAuth();
  const canEdit = can('configuracion.editar');

  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [config,   setConfig]   = useState({
    nombre_negocio:      '',
    moneda:              'ARS',
    moneda_simbolo:      '$',
    stock_alerta_activo: '1',
  });

  // Campos de solo lectura (informativos)
  const [readonly, setReadonly] = useState({
    correlativo_ventas:  '0',
    correlativo_compras: '0',
    version:             '',
  });

  useEffect(() => {
    api.get('/configuracion/index.php')
      .then(res => {
        const d = res.data;
        setConfig({
          nombre_negocio:      d.nombre_negocio      ?? '',
          moneda:              d.moneda               ?? 'ARS',
          moneda_simbolo:      d.moneda_simbolo       ?? '$',
          stock_alerta_activo: d.stock_alerta_activo  ?? '1',
        });
        setReadonly({
          correlativo_ventas:  d.correlativo_ventas  ?? '0',
          correlativo_compras: d.correlativo_compras ?? '0',
          version:             d.version             ?? '',
        });
      })
      .catch(() => toast.error('Error al cargar configuración'))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (v) => setConfig(c => ({ ...c, [k]: v }));
  const setEv = (k) => (e) => set(k)(e.target.value);

  // Al cambiar la moneda predefinida, auto-rellenar el símbolo
  const handleMonedaChange = (code) => {
    const m = MONEDAS.find(x => x.code === code);
    setConfig(c => ({ ...c, moneda: code, moneda_simbolo: m?.simbolo ?? c.moneda_simbolo }));
  };

  const handleSave = async () => {
    if (!config.nombre_negocio.trim()) { toast.error('El nombre del negocio es requerido'); return; }
    if (!config.moneda_simbolo.trim()) { toast.error('El símbolo de moneda es requerido'); return; }
    setSaving(true);
    try {
      await api.put('/configuracion/index.php', {
        nombre_negocio:      config.nombre_negocio.trim(),
        moneda:              config.moneda,
        moneda_simbolo:      config.moneda_simbolo.trim(),
        stock_alerta_activo: config.stock_alerta_activo,
      });
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-slate-300 dark:text-slate-600">
      <RefreshCw size={22} className="animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="page-header">
        <h1 className="page-title">Parámetros del sistema</h1>
        {canEdit && (
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={16} />
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        )}
      </div>

      {/* Datos del negocio */}
      <SectionCard
        icon={Building2}
        iconBg="bg-primary-50 dark:bg-primary-500/10"
        iconColor="text-primary-500 dark:text-primary-400"
        title="Datos del negocio"
        subtitle="Información general que aparece en documentos e informes"
      >
        <div>
          <label>Nombre del negocio *</label>
          <input
            className="input"
            placeholder="Ej: Pollería Ramírez"
            value={config.nombre_negocio}
            onChange={setEv('nombre_negocio')}
            disabled={!canEdit}
          />
        </div>
      </SectionCard>

      {/* Moneda */}
      <SectionCard
        icon={DollarSign}
        iconBg="bg-emerald-50 dark:bg-emerald-500/10"
        iconColor="text-emerald-500 dark:text-emerald-400"
        title="Moneda"
        subtitle="Moneda utilizada en precios y totales"
      >
        <div className="space-y-4">
          <div>
            <label>Moneda *</label>
            <select
              className="input"
              value={config.moneda}
              onChange={(e) => handleMonedaChange(e.target.value)}
              disabled={!canEdit}
            >
              {MONEDAS.map(m => (
                <option key={m.code} value={m.code}>
                  {m.code} — {m.label}
                </option>
              ))}
              <option value="custom">Otra...</option>
            </select>
          </div>
          <div>
            <label>Símbolo *</label>
            <div className="flex items-center gap-3">
              <input
                className="input w-28 font-mono text-center text-lg"
                placeholder="$"
                value={config.moneda_simbolo}
                onChange={setEv('moneda_simbolo')}
                disabled={!canEdit}
                maxLength={5}
              />
              <p className="text-xs text-slate-400">
                Ejemplo con símbolo actual: <span className="font-semibold text-slate-600 dark:text-slate-300">{config.moneda_simbolo} 1.500,00</span>
              </p>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Stock */}
      <SectionCard
        icon={Bell}
        iconBg="bg-amber-50 dark:bg-amber-500/10"
        iconColor="text-amber-500 dark:text-amber-400"
        title="Stock"
        subtitle="Comportamiento de las alertas de inventario"
      >
        <Toggle
          checked={config.stock_alerta_activo === '1'}
          onChange={(v) => set('stock_alerta_activo')(v ? '1' : '0')}
          label="Alertas de bajo stock"
          description="Muestra advertencias en el dashboard cuando un producto cae por debajo del stock mínimo"
        />
      </SectionCard>

      {/* Solo lectura */}
      <SectionCard
        icon={Info}
        iconBg="bg-slate-100 dark:bg-slate-700"
        iconColor="text-slate-500 dark:text-slate-400"
        title="Información del sistema"
        subtitle="Valores de solo lectura — no se pueden editar manualmente"
      >
        <div className="space-y-3">
          {[
            { label: 'Versión del schema',         value: readonly.version             || '—' },
            { label: 'Último N.° de venta',        value: `#${readonly.correlativo_ventas.padStart(4, '0')}` },
            { label: 'Último N.° de compra',       value: `#${readonly.correlativo_compras.padStart(4, '0')}` },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700 last:border-0">
              <span className="text-[13px] text-slate-500 dark:text-slate-400">{label}</span>
              <span className="text-[13px] font-semibold font-mono text-slate-700 dark:text-slate-300">{value}</span>
            </div>
          ))}
        </div>
      </SectionCard>

      {canEdit && (
        <div className="flex justify-end">
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={16} />
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      )}
    </div>
  );
}
